import OktaAuth, { toRelativeUrl } from '@okta/okta-auth-js'
import { LoginCallback, SecureRoute, Security } from '@okta/okta-react'
import * as Sentry from '@sentry/react'
import theme from 'app.json'
import 'bootstrap/dist/css/bootstrap.min.css'
import { createBrowserHistory } from 'history'
import About from 'pages/Home/About'
import Error from 'pages/Home/Error'
import Home from 'pages/Home/Home'
import KlantKoppelingenLijst from 'pages/Klanten/KlantKoppelingenLijst'
import KlantGroepenLijst from 'pages/Klantgroepen/KlantGroepenLijst'
import AarCombinatiesLijst from 'pages/Locatietoewijzing/AarCombinatiesLijst'
import AarZonderLocatieLijst from 'pages/Locatietoewijzing/AarZonderLocatieLijst'
import AflAdrLosseKopersLijst from 'pages/Locatietoewijzing/AflAdrLosseKopersLijst'
import CustomerDeliveryLocationsList from 'pages/Locatietoewijzing/CustomerDeliveryLocationsList'
import GlnLocatiesPerVestigingenLijst from 'pages/Locatietoewijzing/GlnLocatiesPerVestigingLijst'
import GlnLocatieZoekLijst from 'pages/Locatietoewijzing/GlnLocatieZoekLijst'
import KoppelingenVestigingenLijst from 'pages/Locatietoewijzing/KoppelingenVestigingenLijst'
import ProductafmetingenList from 'pages/ProductDimension/ProductDimensionList'
import VolumewaardenLijst from 'pages/Volumewaarden/VolumewaardenLijst'
import * as React from 'react'
import { Provider } from 'react-redux'
import { Redirect, Route, Router, Switch, useHistory } from 'react-router-dom'
import { configureStore } from 'redux/store/store'
import Footer from 'shared/components/Footer'
import ProtectedRoute from 'shared/components/ProtectedRoute'
import TopMenu from 'shared/components/TopMenu'
import { UrlLinks } from 'shared/Constants'
import { RoleNames } from 'shared/models/Enum'
import Apps from 'themes/AppsIndex'
import 'themes/thema1/styles/main.scss'

const browserHistory = createBrowserHistory()

Sentry.init({
  dsn: 'https://4c3bf4d89778df8387d8de95d1be0ee6@o4508097378975744.ingest.de.sentry.io/4509887827607633',
  enabled: process.env.REACT_APP_ENV !== 'dev',
  environment: process.env.REACT_APP_ENV,
  integrations: [
    Sentry.reactRouterV5BrowserTracingIntegration({ history: browserHistory }),
  ],
})

const store = configureStore()

export const oktaAuthConfig = new OktaAuth({
  issuer: process.env.REACT_APP_OKTA_DOMAIN,
  clientId: process.env.REACT_APP_OKTA_CLIENT_ID,
  redirectUri: `${window.location.origin}/callback`,
  pkce: true,
  scopes: ['openid', 'profile', 'email', process.env.REACT_APP_OKTA_SCOPE],
  devMode: process.env.REACT_APP_ENV === 'dev',
  tokenManager: {
    storage: 'localStorage',
    autoRenew: true,
    expireEarlySeconds: 60,
    syncStorage: true,
  },
})

const App = () => {
  return (
    <div className='site-container'>
      <TopMenu />
      <div className='site-content'>
        <Switch>
          <Redirect exact from='/' to='/home' />
          <Route path='/callback' component={LoginCallback} />
          <SecureRoute exact path='/home' component={Home} />
          <ProtectedRoute
            path='/locatie'
            component={AflAdrLosseKopersLijst}
            requiredRole={RoleNames.LboGlnOnderhouden}
          />
          <ProtectedRoute
            path='/combinaties'
            component={AarCombinatiesLijst}
            requiredRole={RoleNames.LboGlnOnderhouden}
          />
          <ProtectedRoute
            path='/tijdvakkenOnderhouden'
            component={KlantGroepenLijst}
            requiredRole={RoleNames.LboKlantGroepenOnderhouden}
          />
          <ProtectedRoute
            path='/tijdvakkenKoppelen'
            component={KlantKoppelingenLijst}
            requiredRole={RoleNames.LboGlnOnderhouden}
          />
          <ProtectedRoute
            path='/volumewaarden'
            component={VolumewaardenLijst}
            requiredRole={RoleNames.LboGlnOnderhouden}
          />
          <ProtectedRoute
            path='/productafmetingen'
            component={ProductafmetingenList}
            requiredRole={RoleNames.LboGlnOnderhouden}
          />
          <ProtectedRoute
            path='/koppelingenvestigingen'
            component={KoppelingenVestigingenLijst}
            requiredRole={RoleNames.LboGlnOnderhouden}
          />
          <ProtectedRoute
            path='/locatiesvestigingen'
            component={GlnLocatiesPerVestigingenLijst}
            requiredRole={RoleNames.LboGlnOnderhouden}
          />
          <ProtectedRoute
            path={UrlLinks.customerLocationLinks}
            component={GlnLocatieZoekLijst}
            requiredRole={RoleNames.LboGlnOnderhouden}
          />
          <ProtectedRoute
            path='/geenkoppeling'
            component={AarZonderLocatieLijst}
            requiredRole={RoleNames.LboGlnOnderhouden}
          />
          <ProtectedRoute
            path='/afleverlocatiesklanten'
            component={CustomerDeliveryLocationsList}
            requiredRole={RoleNames.LboGlnOnderhouden}
          />
          <SecureRoute path='/about' component={About} />
          // @ts-ignore
          <SecureRoute exact render={Apps[theme.themename].render} />
        </Switch>
      </div>
      <Footer />
    </div>
  )
}

const AppWithRouter = () => {
  const history = useHistory()

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    try {
      console.log('restoreOriginalUri called with:', originalUri)

      if (!history) {
        console.error('History object is undefined')
        window.location.href = originalUri || '/'
        return
      }

      const url = toRelativeUrl(originalUri || '/', window.location.origin)
      console.log('Redirecting to:', url)

      history.replace(url)
    } catch (error) {
      console.error('Error in restoreOriginalUri:', error)
      // Fallback to window.location
      window.location.href = originalUri || '/'
    }
  }

  return (
    <Provider store={store}>
      <Router history={browserHistory}>
        <Sentry.ErrorBoundary
          fallback={({ error, resetError }) => (
            <div className='site-container'>
              <TopMenu />
              <div className='site-content'>
                <Error error={error as Error} resetError={resetError} />
              </div>
              <Footer />
            </div>
          )}
        >
          <Security
            oktaAuth={oktaAuthConfig}
            restoreOriginalUri={restoreOriginalUri}
          >
            <App />
          </Security>
        </Sentry.ErrorBoundary>
      </Router>
    </Provider>
  )
}

export default AppWithRouter
